import React, { useState } from 'react'

import clsx from 'clsx'
import dynamic from 'next/dynamic'

import Autocomplete from 'components/Autocomplete'
import { useEventContext } from 'context/EventContext'
import debounce from 'helpers/debounce'
import { escapeRegexSpecialCharacters } from 'helpers/highlightWords'

import TopicResult from './components/TopicResult'
import useResultContext from '../../ResultsProvider'

import classes from './ResourcesSearch.module.scss'

const PostResult = dynamic(() => import('./components/PostResult'))

/** @type {import("./types").default} */
const ResourcesSearch = () => {
  const [options, setOptions] = useState([])
  const [query, setQuery] = useState('')
  const { sendEvent } = useEventContext({ engagementComponent: 'search-engine' })
  const { setHovered, timeoutRef } = useResultContext()
  const regex = new RegExp(`(${query.trim()})`, 'gi')

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setHovered('')
  }

  const onChangeText = async value => {
    if (value.length < 3) {
      setOptions([])
      return
    }

    setQuery(escapeRegexSpecialCharacters(value))
    let results = []
    try {
      const queryParams = new URLSearchParams({ q: value })
      const response = await fetch(`/apiui/resourcesSearch/?${queryParams.toString()}`)
      results = await response.json()
    } finally {
      setOptions(results.length ? results : [{ noResults: true, disabled: true }])
    }
  }

  const onClear = () => {
    setOptions([])
    setHovered('')
  }

  const onFocus = () => {
    setHovered('')
    sendEvent({
      engagementDestination: 'search_select-search-bar',
      engagementDetails: 'search-bar',
      name: 'engagement_search',
    })
  }

  return (
    <Autocomplete
      className={clsx('ResourcesSearch', classes.root)}
      options={options}
      componentsClasses={{ listbox: classes.listbox }}
      onChange={debounce(onChangeText, 300)}
      onFocus={onFocus}
      groupBy={option => option.type}
      getLabel={option => option.name || option.title}
      renderOption={option => {
        if (option.noResults) {
          return (
            <div>
              <div className={classes.noResultsTitle}>Sorry, we couldn’t find any matches.</div>
              <div className={classes.noResultsText}>
                Make sure your keywords are spelled correctly or try another search.
              </div>
            </div>
          )
        }
        if (option.type === 'Articles') {
          return <PostResult option={option} regex={regex} />
        }

        if (option.type === 'Topics') {
          return <TopicResult option={option} />
        }
      }}
      renderGroup={params => {
        return (
          <>
            <div className={classes.group}>
              {params.group}
              <div className={classes.divider} />
            </div>
            <div
              onMouseLeave={handleMouseLeave}
              className={clsx(params.group === 'Topics' && classes.topicsGroup)}
              role="list"
            >
              {params.children}
            </div>
          </>
        )
      }}
      inputProps={{ placeholder: 'Search for an article or topic...', placeholderCentered: true }}
      onClear={onClear}
      closeOnSelect
      getOptionDisabled={option => option.disabled}
    />
  )
}

export default ResourcesSearch
